/**
 * Tipos de domínio do Azim CRM.
 * Shape compatível com o contrato futuro do azim-api.
 */

export type AccountHealth = "good" | "watch" | "risk";
/**
 * @deprecated Usado apenas pelo modelo mock legado `Account` (dashboard/pipeline
 * demo) e pelo mapa `accountTiers` de segmento do Kanban (`Record<accountId, string>`).
 * `AccountListItem` (features/accounts/types.ts) NÃO usa mais este tipo — desde
 * STORY-06 (accounts-list-enrichment) o tier de conta é uma entidade configurável
 * por tenant, ver `AccountTierOption`.
 */
export type AccountTier = "Enterprise" | "Mid-Market" | "SMB";

/**
 * Segmentação de conta configurável por tenant (ADR-0035, epic
 * accounts-list-enrichment). Substitui a união fixa `AccountTier` para o campo
 * de tier de `AccountListItem` — vem de `GET /account-tiers`, com soft-delete
 * via `active` (tiers arquivados preservam o rótulo em contas já atribuídas,
 * mas somem do filtro/seletor).
 */
export interface AccountTierOption {
  readonly id: string;
  readonly label: string;
  readonly position: number;
  readonly active: boolean;
}
export type OpportunityStage = "lead" | "qualify" | "propose" | "negotiate" | "won" | "lost";
export type ActivityState = "upcoming" | "due" | "overdue" | "done";
export type ActivityType = "call" | "email" | "meeting" | "task";
/**
 * Vocabulário canônico de papéis RBAC (ADR-0015).
 * Identificadores em inglês snake_case — estáveis como literais de claim JWT.
 * A flag `isMaster` em `CurrentUser` deriva de `role === 'platform_operator'`.
 */
export type UserRole =
  | "platform_operator"
  | "tenant_admin"
  | "bu_manager"
  | "sales_ae"
  | "sales_sdr"
  | "sales_se"
  | "viewer";

/**
 * Mapa de rótulos de exibição em português brasileiro (ADR-0015, §2; D-22 do
 * change `role-bu-scope`, TASK-18).
 * Use para qualquer elemento de UI que precise mostrar o papel ao usuário.
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  platform_operator: "Operador de Plataforma",
  tenant_admin: "Administrador",
  bu_manager: "Gestor da unidade",
  sales_ae: "Executivo de contas",
  sales_sdr: "Pré-vendas",
  sales_se: "Especialista técnico",
  viewer: "Somente leitura",
};
export type UserStatus = "active" | "invited" | "suspended" | "deactivated";
export type TenantStatus = "active" | "trial" | "past_due" | "suspended";
export type BUStatus = "active" | "inactive";
export type PartnerStatus = "active" | "review" | "inactive";

export interface Owner {
  id: string;
  name: string;
  initials: string;
  color: string;
  role: string;
}

export interface Stage {
  id: string;
  label: string;
  color: string;
}

export interface Account {
  id: string;
  name: string;
  domain: string;
  segment: string;
  tier: AccountTier;
  city: string;
  employees: string;
  owner: string;
  health: AccountHealth;
  arr: number;
  openOpps: number;
  logoTone: string;
  logoText?: string;
}

export interface Opportunity {
  id: string;
  name: string;
  account: string;
  stage: OpportunityStage;
  value: number;
  prob: number;
  owner: string;
  close: string;
  products: string[];
  age: number;
  nextStep: string;
  priority: "high" | "med" | "low";
}

export interface Activity {
  id: string;
  type: ActivityType;
  title: string;
  account: string;
  opp?: string;
  owner: string;
  due: string;
  state: ActivityState;
  priority: "high" | "med" | "low";
}

export interface InboxItem {
  id: string;
  kind: "reply" | "ai" | "meeting" | "note" | "won";
  account: string;
  who: string;
  preview: string;
  time: string;
  unread: boolean;
  owner: string;
}

export interface KPIs {
  pipelineTotal: number;
  weighted: number;
  wonQuarter: number;
  quotaQuarter: number;
  winRate: number;
  avgCycle: number;
  openCount: number;
}

export interface TrendItem {
  m: string;
  won: number;
  goal: number;
}

export interface FunnelItem {
  stage: string;
  value: number;
  count: number;
}

export interface CurrentUser {
  id: string;
  name: string;
  displayName: string;
  initials: string;
  color: string;
  email: string;
  phone: string;
  role: string;
  title: string;
  tenant: string;
  timezone: string;
  twoFactor: boolean;
  lastPasswordChange: string;
  isMaster: boolean;
  canSwitchBU: boolean;
  tenantRole: string;
}

export interface User {
  id: string;
  name: string;
  initials: string;
  color: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  last: string;
  tenant: string;
  seats?: number;
  mfa: boolean;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  tz: string;
  myRole: string;
  tone: string;
  plan: string;
  seatsUsed: number;
  seats: number;
  status: TenantStatus;
  admin: string;
  mrr: number;
  region: string;
}

export interface BusinessUnit {
  id: string;
  name: string;
  short: string;
  slug: string;
  tone: string;
  members: number;
  accounts: number;
  created: string;
  status: BUStatus;
}

export interface Partner {
  id: string;
  name: string;
  initials: string;
  tone: string;
  role: string;
  since: number;
  status: PartnerStatus;
  mrr: number;
  setup: number;
  hw: number;
  opps: number;
  ytd: number;
  contacts: number;
  slug: string;
}

export interface RoleDefinition {
  id: string;
  label: string;
  tone: string;
  desc: string;
}

export interface AuditEntry {
  id: string;
  /** Nome legível do autor — não exposto pelo contrato atual; exibe "—" quando ausente. */
  actor?: string;
  /** Papel do usuário — não exposto pelo contrato atual; exibe "—" quando ausente. */
  role?: string;
  /** Tipo de operação: "create" | "update" | "delete" ou valor legado do mock. */
  action: string;
  /** Entidade/alvo da operação — derivado de entityType+entityId quando não fornecido diretamente. */
  target: string;
  /** Severidade calculada — não exposta pelo contrato atual; exibe "—" quando ausente. */
  sev?: "info" | "medium" | "high";
  /** Endereço IP do autor — não exposto pelo contrato atual (RNF 5.3); exibe "—" quando ausente. */
  ip?: string;
  /** Horário formatado para exibição (pt-BR). */
  time: string;
  /** Categoria de negócio — não exposta pelo contrato atual; exibe "—" quando ausente. */
  cat?: string;
}
